/**
 * Environment Configuration System
 * 
 * This module provides comprehensive environment detection and configuration
 * for Firebase services, supporting development, testing, and production environments.
 */

export interface EnvironmentConfig {
  isDevelopment: boolean;
  isProduction: boolean;
  isTesting: boolean;
  useEmulators: boolean;
  enableDebugLogging: boolean;
  firebase: {
    projectId: string;
    region: string;
    useEmulators: boolean;
    emulatorPorts: {
      auth: number;
      firestore: number;
      functions: number;
    };
  };
  api: {
    baseUrl: string;
    functionsUrl: string;
  };
  features: {
    appCheckEnabled: boolean;
    analyticsEnabled: boolean;
    offlinePersistence: boolean;
  };
}

/**
 * Detect the current environment based on various indicators
 */
function detectEnvironment(): {
  isDevelopment: boolean;
  isProduction: boolean;
  isTesting: boolean;
} {
  // Check Vite environment mode
  const viteMode = import.meta.env.MODE;
  const viteDev = import.meta.env.DEV;
  const viteProd = import.meta.env.PROD;
  
  // Check for testing environment
  const isTesting = 
    import.meta.env.VITE_TESTING_MODE === 'true' ||
    typeof window !== 'undefined' && (window as any).__TESTING__ ||
    process.env.NODE_ENV === 'test' ||
    viteMode === 'test';
  
  // Check for development environment
  const isDevelopment = 
    viteDev ||
    viteMode === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('local') ||
    window.location.port !== '';
  
  // Production is everything else
  const isProduction = !isDevelopment && !isTesting;
  
  return {
    isDevelopment,
    isProduction,
    isTesting
  };
}

/**
 * Determine if Firebase emulators should be used
 */
function shouldUseEmulators(isDevelopment: boolean, isTesting: boolean): boolean {
  // Explicit environment variable override
  if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
    return true;
  }
  
  if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'false') {
    return false;
  }
  
  // Default behavior: use emulators in development and testing
  return isDevelopment || isTesting;
}

/**
 * Get Firebase emulator ports from environment or use defaults
 */
function getEmulatorPorts() {
  return {
    auth: parseInt(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT || '9099'),
    firestore: parseInt(import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_PORT || '8081'),
    functions: parseInt(import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT || '5002')
  };
}

/**
 * Validate required environment variables
 */
function validateEnvironmentVariables(): void {
  const requiredVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID'
  ];
  
  const missing = requiredVars.filter(varName => !import.meta.env[varName]);
  
  if (missing.length > 0) {
    const error = new Error(
      `Missing required environment variables: ${missing.join(', ')}\n` +
      'Please check your .env file and ensure all Firebase configuration variables are set.'
    );
    console.error('Environment Configuration Error:', error.message);
    throw error;
  }
}

/**
 * Create the complete environment configuration
 */
function createEnvironmentConfig(): EnvironmentConfig {
  // Validate environment variables first
  validateEnvironmentVariables();
  
  const environment = detectEnvironment();
  const useEmulators = shouldUseEmulators(environment.isDevelopment, environment.isTesting);
  
  const config: EnvironmentConfig = {
    ...environment,
    useEmulators,
    enableDebugLogging: environment.isDevelopment || environment.isTesting,
    
    firebase: {
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      region: import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'europe-west1',
      useEmulators,
      emulatorPorts: getEmulatorPorts()
    },
    
    api: {
      baseUrl: useEmulators 
        ? `http://localhost:${getEmulatorPorts().functions}`
        : (import.meta.env.VITE_API_URL || `https://${import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'europe-west1'}-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`),
      functionsUrl: useEmulators
        ? `http://localhost:${getEmulatorPorts().functions}`
        : (import.meta.env.VITE_FUNCTIONS_URL || `https://${import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'europe-west1'}-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net`)
    },
    
    features: {
      // Disable App Check in development and testing for easier debugging
      appCheckEnabled: environment.isProduction && import.meta.env.VITE_FIREBASE_APP_CHECK_KEY !== undefined,
      
      // Enable analytics in production, optional in development
      analyticsEnabled: environment.isProduction || import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
      
      // Enable offline persistence in production, optional in development
      offlinePersistence: environment.isProduction || import.meta.env.VITE_ENABLE_OFFLINE_PERSISTENCE === 'true'
    }
  };
  
  return config;
}

// Create and export the configuration
export const environmentConfig = createEnvironmentConfig();

/**
 * Debug logging utility that respects environment configuration
 */
export function debugLog(message: string, data?: any, category: string = 'General'): void {
  if (environmentConfig.enableDebugLogging) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${category}]`;
    
    if (data !== undefined) {
      console.log(`${prefix} ${message}`, data);
    } else {
      console.log(`${prefix} ${message}`);
    }
  }
}

/**
 * Environment-aware error logging
 */
export function logError(error: Error, context?: string): void {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [ERROR]`;
  const contextStr = context ? ` [${context}]` : '';
  
  console.error(`${prefix}${contextStr}`, error);
  
  // In production, you might want to send errors to a monitoring service
  if (environmentConfig.isProduction) {
    // TODO: Integrate with error monitoring service (e.g., Sentry)
    // sendErrorToMonitoring(error, context);
  }
}

/**
 * Get environment-specific configuration for Firebase
 */
export function getFirebaseConfig() {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: environmentConfig.firebase.projectId,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  };
}

/**
 * Check if we're running in an admin context
 */
export function isAdminContext(): boolean {
  return typeof window !== 'undefined' && 
         window.location.pathname.startsWith('/admin');
}

/**
 * Check if Firebase services should be initialized
 */
export function shouldInitializeFirebase(): boolean {
  // Always initialize in admin context
  if (isAdminContext()) {
    return true;
  }
  
  // Initialize if user is authenticated
  if (typeof window !== 'undefined') {
    const authKey = `firebase:authUser:${import.meta.env.VITE_FIREBASE_API_KEY}:[DEFAULT]`;
    return localStorage.getItem(authKey) !== null;
  }
  
  return false;
}

/**
 * Get emulator host for a specific service
 */
export function getEmulatorHost(service: 'auth' | 'firestore' | 'functions'): string {
  if (!environmentConfig.useEmulators) {
    throw new Error(`Emulators are not enabled in current environment`);
  }
  
  const port = environmentConfig.firebase.emulatorPorts[service];
  return `localhost:${port}`;
}

/**
 * Print environment configuration summary (for debugging)
 */
export function printEnvironmentSummary(): void {
  if (!environmentConfig.enableDebugLogging) {
    return;
  }
  
  console.group('🔧 Environment Configuration');
  console.log('Environment:', {
    isDevelopment: environmentConfig.isDevelopment,
    isProduction: environmentConfig.isProduction,
    isTesting: environmentConfig.isTesting
  });
  console.log('Firebase:', {
    projectId: environmentConfig.firebase.projectId,
    region: environmentConfig.firebase.region,
    useEmulators: environmentConfig.firebase.useEmulators,
    emulatorPorts: environmentConfig.firebase.emulatorPorts
  });
  console.log('API URLs:', environmentConfig.api);
  console.log('Features:', environmentConfig.features);
  console.log('Current URL:', window.location.href);
  console.log('Should Initialize Firebase:', shouldInitializeFirebase());
  console.groupEnd();
}

// Print configuration summary on module load in development
if (environmentConfig.enableDebugLogging) {
  printEnvironmentSummary();
}